import React, {useState, useEffect, useRef} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import {addBrand} from '../../../modules/products/cors/_requests'
import SharedData from '../../../modules/common/SharedData'

const AddBrandPage: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const {formatMessage} = useIntl()
  const {currentUser, language} = SharedData()
  const [loading, setLoading] = useState(false)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [brandData, setBrandData] = useState({
    name: {
      en: '',
      ar: '',
    },
    image: null as File | null,
  })

  const handleInputChange = (field: string, value: string) => {
    setBrandData((prev) => ({
      ...prev,
      name: {
        ...prev.name,
        [field]: value,
      },
    }))
  }

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null
    setBrandData((prev) => ({
      ...prev,
      image: file,
    }))
    
    // Create preview URL for the new image
    if (file) {
      const previewUrl = URL.createObjectURL(file)
      setImagePreview(previewUrl)
    } else {
      setImagePreview(null)
    }
  }

  // Cleanup preview URL when component unmounts or when image changes
  useEffect(() => {
    return () => {
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview)
      }
    }
  }, [imagePreview])

  const validateForm = () => {
    if (!brandData.name.en || brandData.name.en.length > 64) {
      toast.error('English name is required and must be less than 64 characters')
      return false
    }
    if (!brandData.name.ar || brandData.name.ar.length > 64) {
      toast.error('Arabic name is required and must be less than 64 characters')
      return false
    }
    if (brandData.image && brandData.image.size > 800 * 1024) {
      toast.error('Image size must be less than 800KB')
      return false
    }
    if (brandData.image && !['image/jpeg', 'image/png', 'image/jpg'].includes(brandData.image.type)) {
      toast.error('Image must be jpeg, png, or jpg')
      return false
    }
    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateForm()) return

    setLoading(true)
    try {
      const response = await addBrand(
        language,
        currentUser?.token,
        {
          name: brandData.name,
          image: brandData.image
        }
      )

      if (response?.data?.message) {
        toast.success(response.data.message)
        // Clear the old preview URL
        if (imagePreview) {
          URL.revokeObjectURL(imagePreview)
        }
        // Reset form data
        setBrandData({
          name: {en: '', ar: ''},
          image: null,
        })
        // Clear image preview
        setImagePreview(null)
        // Clear file input
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
        // Reset form
        const form = e.target as HTMLFormElement
        form.reset()
      } else {
        toast.error('Failed to add brand')
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Something went wrong')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <PageTitle breadcrumbs={[]}>{formatMessage({id: 'MENU.BRAND.ADD'})}</PageTitle>
      <div className='card mb-5 mb-xl-10'>
        <div className='card-body py-3'>
          <form onSubmit={handleSubmit}>
            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label required fw-bold fs-6'>
                {formatMessage({id: 'brand_name_en'})}
              </label>
              <div className='col-lg-8'>
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  placeholder={formatMessage({id: 'brand_name_en'})}
                  value={brandData.name.en}
                  onChange={(e) => handleInputChange('en', e.target.value)}
                  required
                />
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label required fw-bold fs-6'>
                {formatMessage({id: 'brand_name_ar'})}
              </label>
              <div className='col-lg-8'>
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  placeholder={formatMessage({id: 'brand_name_ar'})}
                  value={brandData.name.ar}
                  onChange={(e) => handleInputChange('ar', e.target.value)}
                  required
                />
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-bold fs-6'>
                {formatMessage({id: 'image'})}
              </label>
              <div className='col-lg-8'>
                <input
                  ref={fileInputRef}
                  type='file'
                  className='form-control form-control-lg form-control-solid'
                  accept='image/*'
                  onChange={handleImageChange}
                />
                {imagePreview && (
                  <div className='mt-3'>
                    <img 
                      src={imagePreview} 
                      alt="Brand preview" 
                      className='img-fluid'
                      style={{
                        maxHeight: '200px',
                        borderRadius: '8px',
                        boxShadow: '0 0 10px rgba(0,0,0,0.1)'
                      }}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className='card-footer d-flex justify-content-end py-6 px-9'>
              <button
                type='submit'
                className='btn btn-primary'
                disabled={loading}
              >
                {loading && (
                  <span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true'></span>
                )}
                {formatMessage({id: 'submit'})}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  )
}

export default AddBrandPage
